/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useState } from "react";

import { UserInfo } from "@gamedev-web/gamedev-sdk-core";

import { CrmEntity, EntityName } from "../../server/crm/dataService";
import { TestAaaActions } from "./TestAaaActions";
import { TestAMSActions } from "./TestAMSActions";
import { TestBillingAccountActions } from "./TestBillingAccountActions";
import { TestCrmDataList } from "./TestCrmDataList";
import { TestCRMParameterSetter } from "./TestCRMParameterSetter";
import { TestDataDisplay } from "./TestDataDisplay";
import { TestDeveloperStoreActions } from "./TestDeveloperStoreActions";
import { TestEnvVariablesDisplay } from "./TestEnvVariablesDisplay";
import { TestGeneralActions } from "./TestGeneralActions";
import { TestHubPermissions } from "./TestHubPermissions";
import { TestIDXboxActions } from "./TestIDXboxActions";
import { TestCaseMatcher } from "./TestCaseMatcher";
import { TestLogsDisplay } from "./TestLogsDisplay";
import { TestOtherFeaturesActions } from "./TestOtherFeaturesActions";
import { TestPCEnrollmentActions } from "./TestPCEnrollmentActions";
import { TestRedisDisplay } from "./TestRedisDisplay";
import { TestTicketingActions } from "./TestTicketingActions";
import * as styles from "./TestStyle.module.css";

type TestBaseProps = Readonly<{
  userInfo?: UserInfo;
  gameConcepts?: CrmEntity<EntityName.gaming_gameconcept>[];
  pitchConcepts?: CrmEntity<EntityName.ggp_strike>[];
  contact?: CrmEntity<EntityName.contact>;
  account?: CrmEntity<EntityName.account>;
  onboardingHubProfile?: CrmEntity<EntityName.gaming_onboardinghubprofile>;
  onboarding?: CrmEntity<EntityName.gaming_onboarding>;
  pitch?: CrmEntity<EntityName.gaming_pitchprogram>;
  ogcEnvURL?: string;
  testLogs?: string[];
  environmentVariables: any;
  onRefresh?: () => Promise<void>;
}>;

type TabId = "actions" | "crm-data" | "redis" | "logs" | "env";

export const TestBase = ({
  userInfo,
  gameConcepts,
  pitchConcepts,
  contact,
  account,
  onboardingHubProfile,
  onboarding,
  pitch,
  ogcEnvURL,
  testLogs,
  environmentVariables,
  onRefresh,
}: TestBaseProps) => {
  const validTabs: TabId[] = ["actions", "crm-data", "redis", "logs", "env"];
  const getTabFromHash = (): TabId => {
    const hash = window.location.hash.replace("#", "") as TabId;
    return validTabs.includes(hash) ? hash : "actions";
  };

  const [activeTab, setActiveTab] = useState<TabId>(getTabFromHash);

  const switchTab = (tabId: TabId) => {
    setActiveTab(tabId);
    window.location.hash = tabId;
  };

  const tabs: Readonly<{ id: TabId; label: string }[]> = [
    { id: "actions", label: "Actions" },
    { id: "crm-data", label: "CRM Data" },
    { id: "redis", label: "Redis" },
    { id: "logs", label: "Server Logs" },
    { id: "env", label: "Environment" },
  ];

  return (
    <div className={styles.base}>
      <header className={styles.titleBar}>
        <h1 className={styles.title}>Onboarding Hub Test Console</h1>
        <nav className={styles.nav}>
          {tabs.map(tab => (
            <button
              key={tab.id}
              className={activeTab === tab.id ? styles.tabActive : styles.tab}
              onClick={() => switchTab(tab.id)}
            >
              {tab.label}
            </button>
          ))}
          {onRefresh && activeTab !== "redis" && activeTab !== "env" && (
            <button className={styles.testButton} onClick={onRefresh}>
              ↻ Refresh
            </button>
          )}
        </nav>
      </header>
      <div className={styles.tabPanel}>
        {activeTab === "actions" && (
          <div className={styles.actionsGrid}>
            <div className={styles.generalActionsWrapper}>
              <TestGeneralActions />
              <TestDataDisplay
                userInfo={userInfo}
                gameConcepts={gameConcepts}
                pitchConcepts={pitchConcepts}
                contact={contact}
                account={account}
                onboardingHubProfile={onboardingHubProfile}
                onboarding={onboarding}
                pitch={pitch}
                ogcEnvURL={ogcEnvURL}
              />
              <TestCRMParameterSetter
                contact={contact}
                account={account}
                onboardingHubProfile={onboardingHubProfile}
                onboarding={onboarding}
                pitch={pitch}
              />
            </div>
            <TestIDXboxActions email={userInfo?.emailAddress ?? ""} />
            <TestHubPermissions />
            <TestDeveloperStoreActions />
            <TestAaaActions />
            <TestAMSActions />
            <TestPCEnrollmentActions />
            <TestOtherFeaturesActions />
            <TestTicketingActions ogcEnvURL={ogcEnvURL} />
            <TestBillingAccountActions />
            <TestCaseMatcher />
          </div>
        )}
        {activeTab === "crm-data" && (
          <TestCrmDataList
            dataList={[
              { title: "Contact", object: contact },
              { title: "Account", object: account },
              { title: "Onboarding Hub Profile", object: onboardingHubProfile },
              { title: "Onboarding", object: onboarding },
              { title: "Pitch", object: pitch },
              { title: "Game concepts", object: gameConcepts },
              { title: "Pitch concepts", object: pitchConcepts },
            ]}
          />
        )}
        {activeTab === "logs" && <TestLogsDisplay testLogs={testLogs} />}
        {activeTab === "redis" && <TestRedisDisplay />}
        {activeTab === "env" && (
          <TestEnvVariablesDisplay environmentVariables={environmentVariables} />
        )}
      </div>
    </div>
  );
};
